import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import Loader from '@/components/ui/loader'
import { useLanguage } from '@/context/LanguageContext'
import { useToast } from '@/context/ToastContext'
import { useMobiApi } from '@/services/mobi-api'
import {
  CreateTanResponseV2,
  TanConfirmReqV2
} from '@/services/mobi-api/numberv2/types'
import { checkEmail } from '@/utils/helpers'
import useIntl from '@/utils/intl'
import { Loop } from '@mui/icons-material'
import { Divider, TextField } from '@mui/material'
import React, { FunctionComponent, useReducer, useState } from 'react'
import OTPInput from 'react-otp-input'
import { Obj } from '..'

interface Props {
  // next: (req: Obj) => void
  next: (obj: Obj) => void
  back: () => void
}

interface State {
  loading?: boolean
  buttonLoading?: boolean
  otp?: string
  email?: string
  tanLocked?: boolean
  tanIdentifierLocked?: boolean
  dialogOpen?: boolean
  errorMessageMn?: string
  errorMessageEn?: string
  timerId?: NodeJS.Timer
  isTanCreated?: boolean
  tanRes?: CreateTanResponseV2
  counter?: number
}

const EmailInput: FunctionComponent<Props> = ({ next, back }) => {
  const { translate } = useIntl()
  const { showError } = useToast()
  const { locale } = useLanguage()
  const { numberv2 } = useMobiApi()
  const [counter, setCounter] = useState(0)
  const [state, setState] = useReducer(
    (state: State, newState: State) => ({ ...state, ...newState }),
    {
      loading: false,
      buttonLoading: false,
      otp: '',
      email: '',
      tanLocked: false,
      tanIdentifierLocked: false,
      dialogOpen: false,
      isTanCreated: false,
      counter: 0
    }
  )

  const handleTanCreate = async (): Promise<void> => {
    try {
      clearTimer()
      setState({
        loading: true,
        errorMessageEn: undefined,
        errorMessageMn: undefined
      })
      const res = await numberv2.createTanV2(state.email ?? '')

      if (res?.error !== undefined) {
        const {
          messageMn = translate('request.failed'),
          messageEn = translate('request.failed')
        } = res.cause ?? {}
        setState({
          errorMessageMn: messageMn ?? 'error',
          errorMessageEn: messageEn ?? messageMn ?? 'error'
        })
        showError(locale === 'en' ? messageEn : messageMn)
      }
      if (
        res?.response !== null &&
        res?.response !== undefined &&
        res.responseCode === 'SUCCESS'
      ) {
        const emailArr = res.responseDescription.split(' ') ?? []
        const emailStr = emailArr[emailArr.length - 1]
        setState({ tanRes: res?.response, email: emailStr })
        setCounter(res.response.timeLeftInSec)
        startTimer()
      }
      if (res?.responseCode === 'TAN_CREATION_LOCKED') {
        setState({ tanRes: res?.response, tanLocked: true })
      }
      if (res?.responseCode === 'TAN_IDENTIFIER_LOCKED') {
        setState({ tanRes: res?.response, tanIdentifierLocked: true })
      }
    } catch (e) {
      showError(translate('request.failed'))
    } finally {
      setState({ isTanCreated: true, loading: false, otp: '' })
    }
  }

  const handleTanConfirm = async (): Promise<void> => {
    try {
      const req: TanConfirmReqV2 = {
        email: state.email ?? '',
        code: state.otp ?? ''
      }
      const res = await numberv2.confirmTanV2(req)
      if (res !== undefined) {
        if (res.responseCode === 'SUCCESS') {
          next({ email: state.email, instanceId: res.instanceId })
        }
        if (res.responseCode === 'TAN_MISMATCHED') {
          showError(translate('sim.tan_mismatch'))
        }
        if (res.responseCode === 'TAN_NOT_FOUND') {
          showError(translate('sim.tan_mismatch'))
        }
        if (res.responseCode === 'TAN_LOCKED') {
          setState({ otp: '' })
          clearTimer()
          showError(translate('sim.tan_locked'))
        }
        if (res?.error !== undefined) {
          showError(translate('request.failed'))
        }
      } else {
        showError(translate('request.failed'))
      }
    } catch (e) {
      showError(translate('request.failed'))
    } finally {
      setState({ loading: false })
    }
  }

  const startTimer = (): void => {
    const intervalId = setInterval(() => {
      setCounter((prev) => prev - 1)
    }, 1000)
    setState({ timerId: intervalId })
  }
  const clearTimer = (): void => {
    clearInterval(state.timerId)
    setState({ timerId: undefined })
    setCounter(0)
  }
  const renderTimer = (): string => {
    if (counter !== undefined && counter > 0) {
      const min = Math.floor(counter / 60)
      const sec = Math.floor(counter % 60)
      const minStr = min < 10 ? '0' + min.toString() : min.toString()
      const secStr = sec < 10 ? '0' + sec.toString() : sec.toString()

      return minStr + ':' + secStr
    } else {
      return '00:00'
    }
  }
  const renderDuration = (time: number | undefined): string => {
    if (time === undefined || time === null) {
      return '?'
    }
    const hours = Math.floor(time / 3600)
    const minutes = Math.floor((time - hours * 3600) / 60)
    const minutesStr: string =
      minutes < 10 ? '0' + minutes.toString() : minutes.toString()

    if (locale === 'en') {
      return hours > 0
        ? hours.toString() + ' hours and ' + minutesStr + ' minutes'
        : minutesStr + ' minutes'
    } else {
      return hours > 0
        ? hours.toString() + ' цаг ' + minutesStr + ' минутын '
        : minutesStr + ' минутын '
    }
  }
  if (state.loading === true) {
    return (
      <div className='w-full h-96 flex items-center justify-center'>
        <Loader />
      </div>
    )
  }
  if (state.tanLocked === true) {
    return (
      <div className='p-4 py-24 flex-1 flex flex-col items-center gap-4'>
        <p className='text-center w-2/3 text-textMid'>
          {translate('sim.tan_creation_locked').replace(
            '$',
            renderDuration(state.tanRes?.creationLimit?.lockTimeLeftSec)
          )}
        </p>
      </div>
    )
  }
  if (state.tanIdentifierLocked === true) {
    return (
      <div className='p-4 py-24 flex-1 flex flex-col items-center gap-4 text-textMid'>
        {translate('sim.tan_creation_locked').replace(
          '$',
          renderDuration(state.tanRes?.timeLeftInSec)
        )}
      </div>
    )
  }
  // if (state.tanRes === undefined) {
  //   return <div className='p-4 py-24 flex-1 flex flex-col items-center gap-4'>{locale === 'mn' ? state.errorMessageMn : state.errorMessageEn ?? translate('request.failed')}</div>
  // }

  return (
    <div className='bg-surfaceMid rounded-xl p-6 md:p-8 flex flex-col items-center justify-center gap-4 transition-all duration-700 border-secondaryMidStroke'>
      <TextField
        label={translate('email')}
        variant='outlined'
        className='w-full rounded-lg border-secondaryHighStroke bg-surfaceInverse text-textMid'
        value={state.email}
        onChange={(e) => setState({ email: e.target.value })}
        InputLabelProps={{
          style: { color: 'var(--color-text-textHigh)' }
        }}
        InputProps={{
          style: {
            color: 'var(--color-text-textHigh)',
            '&::placeholder': {
              color: 'var(--color-text-secondaryHighStroke)'
            }
          } as any
        }}
        sx={{
          '& fieldset': {
            borderRadius: '8px'
          }
        }}
      />
      {state.isTanCreated !== null && state.isTanCreated === true ? (
        <div className='flex flex-col items-center justify-center gap-4 w-full'>
          <p className='text-textMid font-medium text-center md:w-4/5'>
            {translate('sim.tan_sent').replace('$email', state.email ?? '')}
          </p>
          <div className='w-full flex justify-center h-fit'>
            <OTPInput
              value={state.otp}
              onChange={(otp) => setState({ otp })}
              numInputs={6}
              renderSeparator={<Divider orientation='vertical' />}
              renderInput={(props: any) => <input {...props} />}
              inputType='number'
              inputStyle={{
                width: '45px',
                height: '45px',
                fontSize: '18px',
                color: 'textHigh',
                fontWeight: '700',
                caretColor: 'blue',
                borderRadius: '8px',
                backgroundColor: 'surfaceInverse'
              }}
              containerStyle={{
                border: '1px solid #CFD3DB',
                borderRadius: '8px',
                outline: 'none',
                backgroundColor: 'surfaceInverse'
              }}
            />
          </div>
          {state.tanRes !== undefined && (
            <span className='text-textMid text-sm'>{renderTimer()}</span>
          )}
          <DarkButton
            type='outlined'
            onClick={() => {
              void handleTanCreate()
            }}
            leadingIcon={<Loop />}
          >
            {translate('sim.tan.getagain')}
          </DarkButton>
          <DarkButton
            onClick={() => {
              void handleTanConfirm()
            }}
            fullwidth
          >
            {translate('continue')}
          </DarkButton>
        </div>
      ) : (
        <DarkButton
          onClick={() => {
            void handleTanCreate()
          }}
          disabled={!checkEmail(state.email ?? '')}
          fullwidth
        >
          {translate('continue')}
        </DarkButton>
      )}
    </div>
  )
}
export default EmailInput
